import axios from "axios";

const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL_DATA?.trim();

if (!API_BASE_URL) {
  throw new Error(
    "VUE_APP_API_BASE_URL_DATA is not configured."
  );
}


// -----------------------------------------------------
// DEPLOYMENT SETTINGS
// -----------------------------------------------------

const DEPLOYMENT_MODE =
  String(
    process.env
      .VUE_APP_DEPLOYMENT_MODE ??
    ""
  )
    .trim()
    .toLowerCase();

const EDGE_TENANT_HOSTNAME =
  String(
    process.env
      .VUE_APP_EDGE_TENANT_HOSTNAME ??
    ""
  )
    .trim()
    .toLowerCase();


// -----------------------------------------------------
// DEBUG BUILD CONFIGURATION
// -----------------------------------------------------

console.log(
  "[publicApi] deployment configuration",
  {
    deploymentMode:
      DEPLOYMENT_MODE,

    edgeTenantHostname:
      EDGE_TENANT_HOSTNAME,

    apiBaseUrl:
      API_BASE_URL,

    browserHostname:
      window.location.hostname
  }
);


// -----------------------------------------------------
// RESOLVE TENANT
// -----------------------------------------------------

function getTenant() {
  // ---------------------------------------------------
  // EDGE DEPLOYMENT
  // ---------------------------------------------------

  if (
    DEPLOYMENT_MODE ===
    "edge"
  ) {
    if (
      EDGE_TENANT_HOSTNAME
    ) {
      return (
        EDGE_TENANT_HOSTNAME
      );
    }

    console.warn(
      "[publicApi] Edge deployment detected, " +
      "but VUE_APP_EDGE_TENANT_HOSTNAME " +
      "is empty."
    );

    return null;
  }


  // ---------------------------------------------------
  // CLOUD / NORMAL DEPLOYMENT
  // ---------------------------------------------------

  let hostname =
    window.location.hostname
      ?.trim()
      .toLowerCase();

  if (!hostname) {
    return null;
  }


  // ---------------------------------------------------
  // REMOVE WWW
  // ---------------------------------------------------

  if (
    hostname.startsWith(
      "www."
    )
  ) {
    hostname =
      hostname.substring(4);
  }


  // ---------------------------------------------------
  // LOCAL DEVELOPMENT
  // ---------------------------------------------------

  if (
    hostname ===
      "localhost" ||
    hostname ===
      "127.0.0.1"
  ) {
    return null;
  }


  return hostname;
}


// -----------------------------------------------------
// AXIOS INSTANCE
// -----------------------------------------------------

const publicApi =
  axios.create({
    baseURL:
      API_BASE_URL,

    timeout:
      30000,

    headers: {
      Accept:
        "application/json"
    }
  });


// -----------------------------------------------------
// REQUEST INTERCEPTOR
// -----------------------------------------------------
//
// Public API client:
//
// - no access token
// - no refresh token
// - no user ID
// - no hospital ID
// - tenant hostname only
//
// Edge:
//
// Browser:
//   localhost
//
// Tenant:
//   nexgbits.com
//
// Header:
//   X-Tenant: nexgbits.com
// -----------------------------------------------------

publicApi.interceptors.request.use(
  config => {
    const tenant =
      getTenant();


    // -------------------------------------------------
    // AXIOS HEADERS OBJECT
    // -------------------------------------------------

    if (
      typeof config.headers?.set ===
      "function"
    ) {
      // -----------------------------------------------
      // TENANT
      // -----------------------------------------------

      if (tenant) {
        config.headers.set(
          "X-Tenant",
          tenant
        );
      }
      else {
        config.headers.delete(
          "X-Tenant"
        );
      }


      // -----------------------------------------------
      // REMOVE AUTHENTICATED CONTEXT
      // -----------------------------------------------

      config.headers.delete(
        "Authorization"
      );

      config.headers.delete(
        "X-TenantId"
      );

      config.headers.delete(
        "X-HospitalId"
      );

      config.headers.delete(
        "X-UserId"
      );


      // -----------------------------------------------
      // REMOVE CONTENT-TYPE FOR GET / DELETE
      // -----------------------------------------------

      if (
        config.method
          ?.toLowerCase() ===
          "get" ||
        config.method
          ?.toLowerCase() ===
          "delete"
      ) {
        config.headers.delete(
          "Content-Type"
        );
      }
    }


    // -------------------------------------------------
    // PLAIN HEADERS OBJECT
    // -------------------------------------------------

    else {
      config.headers =
        config.headers ||
        {};


      // -----------------------------------------------
      // TENANT
      // -----------------------------------------------

      if (tenant) {
        config.headers[
          "X-Tenant"
        ] =
          tenant;
      }
      else {
        delete config.headers[
          "X-Tenant"
        ];
      }


      // -----------------------------------------------
      // REMOVE AUTHENTICATED CONTEXT
      // -----------------------------------------------

      delete config.headers
        .Authorization;

      delete config.headers
        .authorization;

      delete config.headers[
        "X-TenantId"
      ];

      delete config.headers[
        "X-HospitalId"
      ];

      delete config.headers[
        "X-UserId"
      ];


      // -----------------------------------------------
      // REMOVE CONTENT-TYPE FOR GET / DELETE
      // -----------------------------------------------

      if (
        config.method
          ?.toLowerCase() ===
          "get" ||
        config.method
          ?.toLowerCase() ===
          "delete"
      ) {
        delete config.headers[
          "Content-Type"
        ];

        delete config.headers[
          "content-type"
        ];
      }
    }


    // -------------------------------------------------
    // DEBUG REQUEST
    // -------------------------------------------------

    console.log(
      "[publicApi] outgoing request",
      {
        method:
          config.method
            ?.toUpperCase(),

        url:
          `${config.baseURL || ""}` +
          `${config.url || ""}`,

        deploymentMode:
          DEPLOYMENT_MODE,

        edgeTenantHostname:
          EDGE_TENANT_HOSTNAME,

        browserHostname:
          window.location.hostname,

        resolvedTenant:
          tenant,

        headers:
          typeof config.headers
            ?.toJSON ===
            "function"
            ?
            config.headers.toJSON()
            :
            config.headers
      }
    );


    return config;
  },

  error =>
    Promise.reject(
      error
    )
);


// -----------------------------------------------------
// RESPONSE INTERCEPTOR
// -----------------------------------------------------

publicApi.interceptors.response.use(
  response =>
    response,

  error => {
    console.error(
      "[publicApi] request failed",
      {
        message:
          error.message,

        code:
          error.code,

        method:
          error.config
            ?.method
            ?.toUpperCase(),

        url:
          `${error.config?.baseURL || ""}` +
          `${error.config?.url || ""}`,

        status:
          error.response
            ?.status,

        response:
          error.response
            ?.data
      }
    );

    return Promise.reject(
      error
    );
  }
);


export default publicApi;